/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { Control, useController } from 'react-hook-form';
import { CmdError } from '@commander-services/gui-components';
import { ISimpleSelectOption } from '../ArrayFields/interfaces';
import WaypointIconService from '../../OnlineMapNew/Waypoints/WaypointIconService';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress, { ESCAPE_KEY } from '../../../hooks/useKeyPress';

interface ISimpleSelectProps {
  name: string;
  label: string;
  options?: ISimpleSelectOption[];
  width?: string;
  introDisabled?: boolean;
  control: Control<any>;
  // hidden?: boolean;
  // id: string;
  disabled?: boolean;
  // className?: string;
  // queueId?: string;
  introOption?: string;
  required?: boolean;
  onChangeCallback?: (value: any) => void;
  onFocusCallback?: () => void;
  tooltip?: string;
  labelClassName?: string;
  rowClassName?: string;
  columnClassName?: string;
}

export const getOptionByValue = (
  options: ISimpleSelectOption[],
  optionValue: string
): JSX.Element | null => {
  const option = options.find((item: ISimpleSelectOption) => String(item.value) === optionValue);
  if (option) {
    return (
      <>
        <i
          className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(option.icon || '')} mr-2`}
        />
        {option.item}
      </>
    );
  }
  return null;
};

export default function SimpleSelectWithIcon(props: ISimpleSelectProps): JSX.Element {
  const [options, setOptions] = React.useState<ISimpleSelectOption[]>([]);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const selectWidth = props.width ? props.width : 'col-sm-9';

  const {
    field: { ref, onChange, onBlur, value },
    fieldState: { invalid, error },
  } = useController({
    name: props.name,
    control: props.control,
    defaultValue: '', // or the default value
  });

  React.useEffect(() => {
    if (props.options) {
      setOptions(props.options);
    }
  });

  const clickOutsideRef: React.RefObject<any> = React.useRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsOpen(false);
  });
  useKeyPress(ESCAPE_KEY, () => {
    setIsOpen(false);
  });

  const handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value: eventValue } = event.currentTarget;
    onChange(eventValue);
    if (props.onChangeCallback) {
      props.onChangeCallback(eventValue);
    }
  };

  const handleOnFocus = (event: React.FocusEvent<HTMLSelectElement>) => {
    if (props.onFocusCallback) {
      props.onFocusCallback();
    }
  };

  const renderIntroOption = (introOption: string | undefined) => {
    if (props.introOption) {
      return (
        <option value="" disabled={props.introDisabled}>
          {props.introOption}
        </option>
      );
    }
    return null;
  };

  let errBorder = '';
  if (error) {
    errBorder = 'err-border';
  }

  const handleOpen = (
    event:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLLabelElement, MouseEvent>
  ): boolean => {
    event.preventDefault();

    if (props.disabled) {
      return false;
    }
    setIsOpen(!isOpen);

    return true;
  };

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const eventValue = event.currentTarget.getAttribute('data-value');
    if (eventValue) {
      onChange(eventValue);
      if (props.onChangeCallback) {
        props.onChangeCallback(eventValue);
      }
      setIsOpen(false);
    }
  };

  return (
    <div ref={clickOutsideRef} className={`form-group ${error && 'form-group-error'}`}>
      <div className={props.rowClassName ? props.rowClassName : ''}>
        <label
          className={`${props.labelClassName ? props.labelClassName : 'd-block form-label'}`}
          onClick={handleOpen}
        >
          {props.required && <span className="text-danger required-field-info">*</span>}
          {props.label}
        </label>
        <div className={props.columnClassName ? props.columnClassName : 'd-block'}>
          <div className={`b-dropdown ${isOpen ? 'open' : ''}`}>
            <div
              id={`simpleSelect-${props.name}`}
              data-cy={`simpleSelect-${props.name}`}
              className={`form-control-labels ${errBorder}`}
              onClick={handleOpen}
              title={props.tooltip || ''}
              style={props.disabled ? { color: '#bbbbbb' } : {}}
            >
              {value && props.options ? getOptionByValue(props.options, value) : props.introOption}
            </div>
            {isOpen && !props.disabled && (
              <div className="b-dropdown__content" style={{ maxHeight: '270px', overflow: 'auto' }}>
                <ul className="b-dropdown__list simple-list">
                  {options &&
                    options.map((option: ISimpleSelectOption) => (
                      <li key={`key-${props.name}-${option.value}`}>
                        <a
                          href="#"
                          title={option.tooltip || option.item}
                          className="item"
                          data-cy={`simpleSelect-${props.name}-${option.item}`}
                          data-value={option.value}
                          onClick={handleOnClick}
                          style={
                            String(option.value) === String(value)
                              ? {
                                  backgroundColor: '#D1E9FF',
                                }
                              : {}
                          }
                        >
                          <i
                            className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(
                              option.icon || ''
                            )} mr-2`}
                          />
                          {option.item} {option.label ? `(${option.label})` : ''}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {error && <CmdError message={error.message} />}
          </div>
        </div>
      </div>
    </div>
  );
}
